import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import CassetteLogo from '../components/CassetteLogo';
import LanguageModal from '../components/LanguageModal';
import Footer from '../components/Footer';
import SeoHead from '../components/SeoHead';
import { MUSIC_SEO } from '../seo/seoConfig';
import { landingJsonLd } from '../seo/jsonLd';
import { 
  GlobeAltIcon, 
  SunIcon, 
  MoonIcon,
  ChartBarIcon,
  FolderIcon,
  MusicalNoteIcon,
  DevicePhoneMobileIcon,
  LockClosedIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import { useDesignMode } from '../contexts/DesignContext';
import { SketchButton } from '../components/sketchy';
import LandingSignInButton from '../components/LandingSignInButton';
import { classNames } from '../utils/classNames';
import { goToSignIn } from '../utils/landingSignIn';

const Landing: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [languageOpen, setLanguageOpen] = useState(false);
  const { isSketchy } = useDesignMode();
  const isDarkMode = theme === 'dark';

  // Authenticated users are redirected by rootIndexLoader before this renders.

  // Apply theme to DOM immediately
  React.useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Prevent scrolling past bounds
  React.useEffect(() => {
    const mainElement = document.querySelector('main');
    if (!mainElement) return;

    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.scrollTop <= 0) {
        target.scrollTop = 0;
      }
    };

    mainElement.addEventListener('scroll', handleScroll);
    return () => mainElement.removeEventListener('scroll', handleScroll);
  }, []);

  // (no parallax JS; snap scrolling handled via CSS)

  const toggleDarkMode = () => {
    toggleTheme();
  };


  const themeClasses = isSketchy
    ? 'paper-bg text-[var(--color-text)]'
    : isDarkMode 
      ? 'bg-[var(--color-bg)] text-[var(--color-text)]' 
      : 'bg-white text-[var(--color-text)]';

    
  return (
    <div className={`min-h-screen ${themeClasses}`} style={{ margin: 0, padding: 0 }}>
      <SeoHead meta={MUSIC_SEO.landing} jsonLd={landingJsonLd()} />
      {/* Header */}
      <header className={classNames(
        'sticky top-0 z-40 shadow-sm',
        isSketchy ? 'nav-bar-sketchy border-b-2 border-dashed border-[var(--color-border)]' : `backdrop-blur-sm ${isDarkMode ? 'bg-[var(--color-surface)]/90' : 'bg-white/90'}`
      )}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 px-0">
            {/* Logo */}
            <div className="flex items-center gap-6">
              <div className="flex items-center">
                <div className="flex items-center gap-2">
                  <CassetteLogo className="w-[60px] h-[48px] sm:w-8 sm:h-6" />
                  <span className="hidden sm:inline text-lg font-bold">cassette.diy</span>
                </div>
              </div>
              <nav className="hidden md:flex items-center gap-4 text-sm">
                <a href="#features" className="hover:text-[var(--brand-from)] transition-colors" style={{ color: 'var(--color-muted)' }}>{t('landing.features.title')}</a>
                <a href="#ai" className="hover:text-[var(--brand-from)] transition-colors" style={{ color: 'var(--color-muted)' }}>AI</a>
                <Link to="/docs" className="hover:text-[var(--brand-from)] transition-colors" style={{ color: 'var(--color-muted)' }}>{t('landing.header.docs')}</Link>
              </nav>
            </div>
            
            {/* Right side controls */}
            <div className="flex items-center gap-2 sm:gap-4">
              {isSketchy ? (
                <SketchButton
                  onClick={toggleDarkMode}
                  className="theme-toggle px-3 py-1 text-sm"
                  data-testid="theme-toggle"
                  aria-label={isDarkMode ? t('landing.header.switchToLight') : t('landing.header.switchToDark')}
                >
                  <span className="theme-toggle__icon" aria-hidden>{isDarkMode ? '☀️' : '🌙'}</span>
                  <span className="label-typewriter text-xs">{isDarkMode ? t('theme.day') : t('theme.night')}</span>
                </SketchButton>
              ) : (
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                data-testid="theme-toggle"
                aria-label={isDarkMode ? t('landing.header.switchToLight') : t('landing.header.switchToDark')}
              >
                {isDarkMode ? (
                  <SunIcon className="w-5 h-5" aria-hidden="true" />
                ) : (
                  <MoonIcon className="w-5 h-5" aria-hidden="true" />
                )}
              </button>
              )}
              <button
                onClick={() => setLanguageOpen(true)}
                className="flex items-center gap-2 px-2 sm:px-3 py-2 transition-colors"
                style={{ color: 'var(--color-text)' }}
                data-testid="language-picker"
                aria-label="Change language"
              >
                <GlobeAltIcon className="w-6 h-6 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline text-sm">Language</span>
              </button>
              <LandingSignInButton placement="header" data-testid="auth-open-button" />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content with Normal Scrolling */}
      <main 
        className="w-full"
        role="main"
        aria-label="Landing page content"
      >
        {/* Hero Section */}
        <section 
          className="section-hero flex items-center justify-center text-center relative overflow-hidden py-16 md:py-24"
          aria-labelledby="hero-heading"
        >
          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="mb-12">
              <h1 
                id="hero-heading"
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 gradient-brand-r bg-clip-text text-transparent animate-fade-in leading-tight"
              >
                {t('landing.hero.title')}
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl mb-8 max-w-2xl mx-auto animate-fade-in-delay leading-relaxed" style={{ color: 'var(--color-text)' }}>
                {t('landing.hero.subtitle')}
              </p>

              <div className="mb-10 max-w-xl mx-auto animate-fade-in-delay-2 px-2">
                <SketchButton
                  type="button"
                  onClick={() => goToSignIn(navigate, 'hero')}
                  variant="primary"
                  className="w-full sm:w-auto px-8 py-4 text-base sm:text-lg font-bold shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-transform"
                  data-testid="landing-get-started-hero"
                >
                  {t('landing.hero.gettingStarted.button')}
                </SketchButton>
                <p className="mt-4 text-sm sm:text-base text-[var(--color-muted)] leading-relaxed max-w-lg mx-auto">
                  {t('landing.hero.gettingStarted.hint')}
                </p>
              </div>
              
              {/* Social proof */}
              <div className="mb-10 animate-fade-in-delay-2">
                <p className="text-sm mb-3 font-medium" style={{ color: 'var(--color-muted)' }}>{t('landing.hero.socialProof')}</p>
                <div className="flex justify-center items-center space-x-6 sm:space-x-8">
                  <span className="text-2xl sm:text-3xl">🎵</span>
                  <span className="text-2xl sm:text-3xl">🎶</span>
                  <span className="text-2xl sm:text-3xl">🎤</span>
                  <span className="text-2xl sm:text-3xl">🎸</span>
                  <span className="text-2xl sm:text-3xl">🎹</span>
                </div>
              </div>
            </div>

            {/* Dashboard Screenshot */}
            <div className="w-full max-w-5xl mx-auto shadow-2xl animate-fade-in-delay-4 transition-all duration-300 hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] hover:-translate-y-2">
              <div className="relative bg-white dark:bg-slate-800 p-2 rounded-xl transition-all duration-300 hover:scale-[1.02]">
                <picture>
                  <source
                    srcSet={isDarkMode ? '/landing-screenshots/dashboard-dark-new.webp' : '/landing-screenshots/dashboard-light-new.webp'}
                    type="image/webp"
                  />
                  <img
                    src={isDarkMode ? '/landing-screenshots/dashboard-dark-new.png' : '/landing-screenshots/dashboard-light-new.png'}
                    alt="Cassette dashboard — track grid with status and progress"
                    className="w-full h-auto block rounded-lg transition-transform duration-300"
                    width={1024}
                    height={788}
                    fetchpriority="high"
                    style={{ imageRendering: 'smooth' }}
                  />
                </picture>
              </div>
            </div>
          </div>
        </section>

        {/* Value Proposition Section */}
        <section 
          className="section-light flex items-center justify-center py-16 md:py-20"
        >
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-6 md:mb-8 gradient-brand-r bg-clip-text text-transparent px-4">
              {t('landing.valueProposition.title')}
            </h2>
            <p className="text-base sm:text-lg md:text-xl mb-8 md:mb-12 max-w-3xl mx-auto leading-relaxed px-4" style={{ color: 'var(--color-text)' }}>
              {t('landing.valueProposition.subtitle')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto px-4">
              {/* Track Status Management */}
              <div className="glass-card glass-card-hover p-6">
                <div className="text-center mb-4">
                  <ChartBarIcon className="w-8 h-8 mx-auto mb-4 text-emerald-600 dark:text-emerald-400" />
                  <h3 className="text-xl font-bold mb-2 bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">{t('landing.valueProposition.trackStatus.title')}</h3>
                  <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
                    {t('landing.valueProposition.trackStatus.description')}
                  </p>
                </div>
                <div className="w-full">
                  <div className="relative bg-white dark:bg-slate-800 p-2 rounded-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-lg">
                    <picture>
                      <source
                        srcSet={isDarkMode ? '/landing-screenshots/track-dark-new.webp' : '/landing-screenshots/track-light-new.webp'}
                        type="image/webp"
                      />
                      <img
                        src={isDarkMode ? '/landing-screenshots/track-dark-new.png' : '/landing-screenshots/track-light-new.png'}
                        alt="Track production view — audio assets and upload"
                        className="w-full h-auto block rounded-lg transition-transform duration-300"
                        width={1024}
                        height={736}
                        loading="lazy"
                        style={{ imageRendering: 'smooth' }}
                      />
                    </picture>
                  </div>
                </div>
              </div>

              {/* Asset Management */}
              <div className="glass-card glass-card-hover p-6">
                <div className="text-center mb-4">
                  <FolderIcon className="w-8 h-8 mx-auto mb-4 text-emerald-600 dark:text-emerald-400" />
                  <h3 className="text-xl font-bold mb-2 bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">{t('landing.valueProposition.assetManagement.title')}</h3>
                  <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
                    {t('landing.valueProposition.assetManagement.description')}
                  </p>
                </div>
                <div className="w-full">
                  <div className="relative bg-white dark:bg-slate-800 p-2 rounded-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-lg">
                    <picture>
                      <source
                        srcSet={isDarkMode ? '/landing-screenshots/videos-dark-new.webp' : '/landing-screenshots/videos-light-new.webp'}
                        type="image/webp"
                      />
                      <img
                        src={isDarkMode ? '/landing-screenshots/videos-dark-new.png' : '/landing-screenshots/videos-light-new.png'}
                        alt="Asset management — promo images and vertical videos"
                        className="w-full h-auto block rounded-lg transition-transform duration-300"
                        width={1024}
                        height={736}
                        loading="lazy"
                        style={{ imageRendering: 'smooth' }}
                      />
                    </picture>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="section-tinted flex items-center justify-center relative overflow-hidden py-16 md:py-20" id="features">
            
            <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-6 md:mb-8 gradient-brand-r bg-clip-text text-transparent px-4">{t('landing.features.title')}</h2>
              <p className="text-base sm:text-lg md:text-xl mb-8 md:mb-16 max-w-3xl mx-auto px-4" style={{ color: 'var(--color-text)' }}>
                {t('landing.features.subtitle')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 px-4">
                <div className="glass-card glass-card-hover p-6">
                  <div className="flex items-center mb-4">
                    <MusicalNoteIcon className="w-6 h-6 mr-3 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                    <h3 className="text-xl font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">{t('landing.features.assetsPerTrack.title')}</h3>
                  </div>
                  <ul className="space-y-2 text-sm" style={{ color: 'var(--color-muted)' }}>
                    <li>• {t('landing.features.assetsPerTrack.audio')}</li>
                    <li>• {t('landing.features.assetsPerTrack.artwork')}</li>
                    <li>• {t('landing.features.assetsPerTrack.promo')}</li>
                  </ul>
                </div>
                <div className="glass-card glass-card-hover p-6">
                  <div className="flex items-center mb-4">
                    <ChartBarIcon className="w-6 h-6 mr-3 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                    <h3 className="text-xl font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">{t('landing.features.trackProgress.title')}</h3>
                  </div>
                  <ul className="space-y-2 text-sm" style={{ color: 'var(--color-muted)' }}>
                    <li>• {t('landing.features.trackProgress.kanban')}</li>
                    <li>• {t('landing.features.trackProgress.status')}</li>
                    <li>• {t('landing.features.trackProgress.progress')}</li>
                  </ul>
                </div>
                <div className="glass-card glass-card-hover p-6">
                  <div className="flex items-center mb-4">
                    <DevicePhoneMobileIcon className="w-6 h-6 mr-3 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                    <h3 className="text-xl font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">{t('landing.features.pwa.title')}</h3>
                  </div>
                  <ul className="space-y-2 text-sm" style={{ color: 'var(--color-muted)' }}>
                    <li>• {t('landing.features.pwa.mobile')}</li>
                    <li>• {t('landing.features.pwa.install')}</li>
                    <li>• {t('landing.features.pwa.fast')}</li>
                  </ul>
                </div>
                <div className="glass-card glass-card-hover p-6">
                  <div className="flex items-center mb-4">
                    <LockClosedIcon className="w-6 h-6 mr-3 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                    <h3 className="text-xl font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">{t('landing.features.privacy.title')}</h3>
                  </div>
                  <ul className="space-y-2 text-sm" style={{ color: 'var(--color-muted)' }}>
                    <li>• {t('landing.features.privacy.drive')}</li>
                    <li>• {t('landing.features.privacy.hosted')}</li>
                    <li>• {t('landing.features.privacy.oauth')}</li>
                    <li>• {t('landing.features.privacy.secure')}</li>
                  </ul>
                </div>
                <div className="glass-card glass-card-hover p-6 md:col-span-2 lg:col-span-1">
                  <div className="flex items-center mb-4">
                    <UsersIcon className="w-6 h-6 mr-3 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                    <h3 className="text-xl font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">{t('landing.features.collaboration.title')}</h3>
                  </div>
                  <ul className="space-y-2 text-sm" style={{ color: 'var(--color-muted)' }}>
                    <li>• {t('landing.features.collaboration.share')}</li>
                    <li>• {t('landing.features.collaboration.roles')}</li>
                    <li>• {t('landing.features.collaboration.sync')}</li>
                  </ul>
                </div>
              </div>
            </div>
        </section>

        {/* MCP / AI Section */}
        <section
          id="ai"
          className={classNames(
            'flex items-center justify-center py-16 md:py-20',
            isSketchy ? '' : 'section-light'
          )}
        >
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className={classNames(
              'text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-6 md:mb-8 px-4',
              isSketchy ? 'scribble-underline inline-block' : 'gradient-brand-r bg-clip-text text-transparent'
            )}>
              {t('landing.mcp.title')}
            </h2>
            <p className="text-base sm:text-lg md:text-xl mb-8 max-w-2xl mx-auto px-4" style={{ color: 'var(--color-muted)' }}>
              {t('landing.mcp.subtitle')}
            </p>
            <ul className="text-left max-w-md mx-auto space-y-2 mb-10 text-sm sm:text-base" style={{ color: 'var(--color-text)' }}>
              <li>• {t('landing.mcp.bullet1')}</li>
              <li>• {t('landing.mcp.bullet2')}</li>
              <li>• {t('landing.mcp.bullet3')}</li>
            </ul>
            <div className="flex flex-wrap justify-center gap-3 px-4">
              <Link to="/docs/mcp">
                <SketchButton variant="primary" className="px-6 py-2">
                  {t('landing.mcp.readDocs')}
                </SketchButton>
              </Link>
              <SketchButton onClick={() => goToSignIn(navigate, 'mcp')} className="px-6 py-2">
                {t('landing.mcp.getToken')}
              </SketchButton>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section 
          id="how-it-works" 
          className="section-light flex items-center justify-center py-16 md:py-20"
        >
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 md:mb-8 gradient-brand-r bg-clip-text text-transparent">
                {t('landing.howItWorks.title')}
              </h2>
              <p className="text-xl md:text-2xl max-w-3xl mx-auto" style={{ color: 'var(--color-text)' }}>
                {t('landing.howItWorks.subtitle')}
              </p>
            </div>
            
            {/* Horizontal Timeline */}
            <div className="relative">
              {/* Connecting line - hidden on mobile */}
              <div className="hidden lg:block absolute top-12 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-400 via-green-500 to-emerald-600"></div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4">
                <div className="relative text-center">
                  <div className="relative z-10 w-24 h-24 bg-gradient-to-r from-emerald-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <span className="text-3xl font-bold text-white">1</span>
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-[var(--color-text)]">{t('landing.howItWorks.step1.title')}</h3>
                  <p className="text-base" style={{ color: 'var(--color-muted)' }}>
                    {t('landing.howItWorks.step1.description')}
                  </p>
                </div>
                
                <div className="relative text-center">
                  <div className="relative z-10 w-24 h-24 bg-gradient-to-r from-emerald-600 to-green-700 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <span className="text-3xl font-bold text-white">2</span>
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-[var(--color-text)]">{t('landing.howItWorks.step2.title')}</h3>
                  <p className="text-base" style={{ color: 'var(--color-muted)' }}>
                    {t('landing.howItWorks.step2.description')}
                  </p>
                </div>
                
                <div className="relative text-center">
                  <div className="relative z-10 w-24 h-24 bg-gradient-to-r from-green-600 to-emerald-700 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <span className="text-3xl font-bold text-white">3</span>
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-[var(--color-text)]">{t('landing.howItWorks.step3.title')}</h3>
                  <p className="text-base" style={{ color: 'var(--color-muted)' }}>
                    {t('landing.howItWorks.step3.description')}
                  </p>
                </div>
                
                <div className="relative text-center">
                  <div className="relative z-10 w-24 h-24 bg-gradient-to-r from-green-700 to-emerald-800 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <span className="text-3xl font-bold text-white">4</span>
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-[var(--color-text)]">{t('landing.howItWorks.step4.title')}</h3>
                  <p className="text-base" style={{ color: 'var(--color-muted)' }}>
                    {t('landing.howItWorks.step4.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section 
          id="pricing"
          data-testid="pricing-section"
          className="section-tinted flex items-center justify-center py-16 md:py-20"
        >
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 md:mb-8 gradient-brand-r bg-clip-text text-transparent">
              {t('landing.pricing.title')}
            </h2>
            <p className="text-xl md:text-2xl text-[var(--color-muted)] mb-6">
              {t('landing.pricing.subtitle')}
            </p>
            <p className="text-base text-[var(--color-text)] max-w-3xl mx-auto mb-16 rounded-xl border px-5 py-4" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-hover)' }}>
              {t('landing.pricing.byodValue')}
            </p>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {/* Starter Plan */}
              <div className="glass-card p-6 text-center">
                <h3 className="text-2xl font-bold mb-4">{t('landing.pricing.starter.name')}</h3>
                <div className="text-5xl font-bold mb-4 text-emerald-600 dark:text-emerald-400">{t('landing.pricing.starter.price')}</div>
                <p className="text-lg text-[var(--color-muted)] mb-6">{t('landing.pricing.starter.period')}</p>
                <div className="text-xl font-bold text-[var(--color-text)] mb-2">{t('landing.pricing.starter.tracks')}</div>
                <div className="text-lg text-[var(--color-text)] mb-2">{t('landing.pricing.starter.features')}</div>
                <div className="text-lg text-[var(--color-muted)] mb-2">{t('landing.pricing.starter.collaboration')}</div>
                <div className="text-sm text-emerald-700 dark:text-emerald-400">{t('landing.pricing.starter.storage')}</div>
              </div>
              
              {/* Artist Plan */}
              <div className="glass-card p-6 text-center relative border-2 border-emerald-500/50 dark:border-emerald-400/50">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-emerald-600 to-green-600 text-white px-6 py-2 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap shadow-lg">
                    {t('landing.pricing.artist.badge')}
                  </span>
                </div>
                <h3 className="text-2xl font-bold mb-4">{t('landing.pricing.artist.name')}</h3>
                <div className="text-5xl font-bold mb-4 bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">{t('landing.pricing.artist.price')}</div>
                <p className="text-lg text-[var(--color-muted)] mb-6">{t('landing.pricing.artist.period')}</p>
                <div className="text-xl font-bold text-[var(--color-text)] mb-2">{t('landing.pricing.artist.tracks')}</div>
                <div className="text-lg text-[var(--color-text)] mb-2">{t('landing.pricing.artist.features')}</div>
                <div className="text-sm text-[var(--color-muted)] mb-1">{t('landing.pricing.artist.storage')}</div>
                <div className="text-sm text-emerald-700 dark:text-emerald-400">{t('landing.pricing.artist.cloudAddon')}</div>
              </div>
              
              {/* Studio Plan */}
              <div className="glass-card p-6 text-center relative border-2 border-purple-500/50 dark:border-purple-400/50">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-2 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap shadow-lg">
                    {t('landing.pricing.studio.badge')}
                  </span>
                </div>
                <h3 className="text-2xl font-bold mb-4">{t('landing.pricing.studio.name')}</h3>
                <div className="text-5xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">{t('landing.pricing.studio.price')}</div>
                <p className="text-lg text-[var(--color-muted)] mb-6">{t('landing.pricing.studio.period')}</p>
                <div className="text-xl font-bold text-[var(--color-text)] mb-2">{t('landing.pricing.studio.tracks')}</div>
                <div className="text-lg text-[var(--color-text)] mb-2">{t('landing.pricing.studio.features')}</div>
                <div className="text-sm text-[var(--color-muted)] mb-1">{t('landing.pricing.studio.storage')}</div>
                <div className="text-sm text-purple-700 dark:text-purple-400">{t('landing.pricing.studio.cloudAddon')}</div>
              </div>
            </div>
            
            <p className="text-lg font-semibold mt-12 text-emerald-700 dark:text-emerald-400">{t('landing.pricing.footer')}</p>
          </div>
        </section>

        {/* Testimonials Section */}
        <section 
          id="testimonials"
          className="section-light flex items-center justify-center py-16 md:py-20"
        >
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-6 md:mb-8 gradient-brand-r bg-clip-text text-transparent px-4">
              {t('landing.testimonials.title')}
            </h2>
            <p className="text-base sm:text-lg md:text-xl mb-8 md:mb-16 px-4" style={{ color: 'var(--color-text)' }}>
              {t('landing.testimonials.subtitle')}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 px-4">
              <div className="glass-card glass-card-hover p-8 relative">
                <div className="absolute top-4 left-4 text-4xl text-emerald-500/20 dark:text-emerald-400/20 font-serif leading-none">"</div>
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-emerald-500 to-green-600 flex items-center justify-center text-white font-bold text-lg mr-3 flex-shrink-0">
                    S
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[var(--color-text)]">{t('landing.testimonials.sarah.name')}</h3>
                    <p className="text-sm" style={{ color: 'var(--color-muted)' }}>{t('landing.testimonials.sarah.role')}</p>
                  </div>
                </div>
                <p className="italic relative z-10" style={{ color: 'var(--color-text)' }}>
                  {t('landing.testimonials.sarah.quote')}
                </p>
              </div>
              
              <div className="glass-card glass-card-hover p-8 relative">
                <div className="absolute top-4 left-4 text-4xl text-emerald-500/20 dark:text-emerald-400/20 font-serif leading-none">"</div>
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 flex items-center justify-center text-white font-bold text-lg mr-3 flex-shrink-0">
                    M
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[var(--color-text)]">{t('landing.testimonials.mike.name')}</h3>
                    <p className="text-sm" style={{ color: 'var(--color-muted)' }}>{t('landing.testimonials.mike.role')}</p>
                  </div>
                </div>
                <p className="italic relative z-10" style={{ color: 'var(--color-text)' }}>
                  {t('landing.testimonials.mike.quote')}
                </p>
              </div>
              
              <div className="glass-card glass-card-hover p-8 relative">
                <div className="absolute top-4 left-4 text-4xl text-emerald-500/20 dark:text-emerald-400/20 font-serif leading-none">"</div>
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 flex items-center justify-center text-white font-bold text-lg mr-3 flex-shrink-0">
                    A
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[var(--color-text)]">{t('landing.testimonials.alex.name')}</h3>
                    <p className="text-sm" style={{ color: 'var(--color-muted)' }}>{t('landing.testimonials.alex.role')}</p>
                  </div>
                </div>
                <p className="italic relative z-10" style={{ color: 'var(--color-text)' }}>
                  {t('landing.testimonials.alex.quote')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section 
          className="section-cta flex items-center text-white relative overflow-hidden py-16 md:py-24"
        >
            
            {/* Gradient overlay for depth */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
            
            <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-6 md:mb-8 text-white drop-shadow-lg px-4">
                {t('landing.cta.title')}
              </h2>
              <p className="text-base sm:text-lg md:text-xl mb-8 md:mb-12 max-w-3xl mx-auto text-blue-100 drop-shadow-md px-4">
                {t('landing.cta.subtitle')}
              </p>
              <div className="flex justify-center px-4">
                <button
                  onClick={() => goToSignIn(navigate, 'footer')}
                  className="text-white px-8 py-4 md:px-12 md:py-5 rounded-lg font-semibold hover:opacity-90 transition-all text-base md:text-lg bg-white/20 backdrop-blur-sm border border-white/30 hover:bg-white/30 shadow-xl hover:shadow-2xl"
                >
                  {t('landing.cta.button')}
                </button>
              </div>
              <p className="text-xs sm:text-sm text-blue-200 mt-4 md:mt-6 drop-shadow-sm px-4">
                {t('landing.cta.disclaimer')}
              </p>
            </div>
        </section>

        {/* Footer */}
        <Footer />
      </main>

      {/* Auth Modal */}
      {/* Auth Modal is removed, so this section is no longer needed */}

      {/* Language Modal */}
      <LanguageModal
        open={languageOpen}
        onClose={() => setLanguageOpen(false)}
      />
    </div>
  );
};

export default Landing; 